import React from 'react';
import { Badge } from '@/components/ui/Badge';
import type { AssetGroupFormData } from '@/types/assetGroup';

interface AssetGroupReviewStepProps {
  formData: AssetGroupFormData;
  campaignName: string;
}

export const AssetGroupReviewStep: React.FC<AssetGroupReviewStepProps> = ({
  formData,
  campaignName,
}) => {
  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Review & Create</h3>
        <p className="text-sm text-gray-600">
          Review your asset group settings before creating. All assets will be uploaded to Google Ads.
        </p>
      </div>

      {/* Summary Card */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {/* Campaign */}
        <div className="p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-1">Campaign</h4>
          <p className="text-base text-gray-900">{campaignName}</p>
          <p className="text-xs text-gray-500 mt-1">Performance Max</p>
        </div>

        {/* Basic Info */}
        <div className="p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Basic Information</h4>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Asset Group Name:</span>
            <span className="text-sm font-medium text-gray-900">{formData.name}</span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Status:</span>
            <Badge variant={formData.status === 'ENABLED' ? 'success' : 'warning'}>
              {formData.status.charAt(0).toUpperCase() + formData.status.slice(1).toLowerCase()}
            </Badge>
          </div>

          <div className="flex justify-between items-start">
            <span className="text-sm text-gray-600">Final URL:</span>
            <span className="text-sm font-medium text-gray-900 text-right break-all max-w-[60%]">
              {formData.finalUrl}
            </span>
          </div>
        </div>

        {/* Text Assets */}
        <div className="p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Text Assets</h4>

          {/* Business Name */}
          <div>
            <span className="text-xs text-gray-600 font-medium">Business Name:</span>
            <p className="text-sm text-gray-900">{formData.businessName}</p>
          </div>

          {/* Headlines */}
          <div>
            <span className="text-xs text-gray-600 font-medium">
              Headlines ({formData.headlines.filter((h) => h.trim()).length}):
            </span>
            <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
              {formData.headlines
                .filter((h) => h.trim())
                .map((headline, i) => (
                  <li key={i} className="truncate">
                    {headline}
                  </li>
                ))}
            </ul>
          </div>

          {/* Long Headline */}
          <div>
            <span className="text-xs text-gray-600 font-medium">Long Headline:</span>
            <p className="text-sm text-gray-900">{formData.longHeadline}</p>
          </div>

          {/* Descriptions */}
          <div>
            <span className="text-xs text-gray-600 font-medium">
              Descriptions ({formData.descriptions.filter((d) => d.trim()).length}):
            </span>
            <ul className="list-disc list-inside text-sm text-gray-900 mt-1">
              {formData.descriptions
                .filter((d) => d.trim())
                .map((description, i) => (
                  <li key={i} className="text-sm">
                    {description}
                  </li>
                ))}
            </ul>
          </div>
        </div>

        {/* Image Assets */}
        <div className="p-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Image Assets</h4>

          <div className="flex flex-wrap gap-2">
            <span className="inline-block bg-gray-100 px-3 py-1.5 rounded text-sm text-gray-700">
              <span className="font-medium">{formData.landscapePending.length}</span> landscape
            </span>
            <span className="inline-block bg-gray-100 px-3 py-1.5 rounded text-sm text-gray-700">
              <span className="font-medium">{formData.squarePending.length}</span> square
            </span>
            <span className="inline-block bg-gray-100 px-3 py-1.5 rounded text-sm text-gray-700">
              <span className="font-medium">{formData.logoPending.length}</span> logo
            </span>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">What Happens Next</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Your images will be uploaded to Google Ads</li>
                <li>Text assets will be created and linked to the asset group</li>
                <li>Google will start generating ad combinations automatically</li>
                <li>You can add more assets later through the campaign management page</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Time Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg
            className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-sm text-amber-800">
              <span className="font-medium">This may take a moment.</span>{' '}
              Creating an asset group involves uploading images to Google Ads, which can take up to a minute. Please don't close this window.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
